/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import APIManager from "../../ApiManager/ApiManager";
import {
  FETCH_DOMAIN_APPROVER_COUNTRY_DETAILS_REQUEST,
  FETCH_DOMAIN_APPROVER_COUNTRY_DETAILS_SUCCESS,
  FETCH_DOMAIN_APPROVER_COUNTRY_DETAILS_FAILURE,
} from "./DomainApproverCountryTypes";
import { COMMON_ERROR } from "../ApplicationDetailsError/ApplicationDetailsErrorTypes";

const COUNTRIES_CACHE_KEY = 'countries-cache';
const COUNTRIES_CACHE_TIMESTAMP_KEY = 'countries-cache-timestamp';
const CACHE_DURATION_MS = 5 * 60 * 1000; // 5 minutes

const safeParseCache = () => {
  if (typeof sessionStorage === 'undefined') return null;

  try {
    const cached = sessionStorage.getItem(COUNTRIES_CACHE_KEY);
    const timestamp = sessionStorage.getItem(COUNTRIES_CACHE_TIMESTAMP_KEY);
    
    if (!cached || !timestamp) return null;

    // Check if cache has expired
    const cacheTime = Number.parseInt(timestamp, 10);
    const currentTime = Date.now();
    if (currentTime - cacheTime > CACHE_DURATION_MS) {
      // Cache expired
      sessionStorage.removeItem(COUNTRIES_CACHE_KEY);
      sessionStorage.removeItem(COUNTRIES_CACHE_TIMESTAMP_KEY);
      return null;
    }

    const parsed = JSON.parse(cached);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    console.warn('Failed to parse countries cache', error);
    return null;
  }
};

const persistCache = (countries) => {
  if (typeof sessionStorage === 'undefined') return;

  try {
    sessionStorage.setItem(COUNTRIES_CACHE_KEY, JSON.stringify(countries));
    sessionStorage.setItem(COUNTRIES_CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.warn('Failed to persist countries cache', error);
  }
};

// Clear cache to force refresh
const clearCache = () => {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(COUNTRIES_CACHE_KEY);
    sessionStorage.removeItem(COUNTRIES_CACHE_TIMESTAMP_KEY);
  } catch (error) {
    console.warn('Failed to clear countries cache', error);
  }
};

export const fetchDomainApproverCountriesDetailsRequest = () => {
  return {
    type: FETCH_DOMAIN_APPROVER_COUNTRY_DETAILS_REQUEST,
  };
};

export const fetchDomainApproverCountriesDetailsSuccess = (countriesData) => {
  return {
    type: FETCH_DOMAIN_APPROVER_COUNTRY_DETAILS_SUCCESS,
    payload: countriesData,
  };
};

export const fetchDomainApproverCountriesDetailsFailure = (error) => {
  return {
    type: FETCH_DOMAIN_APPROVER_COUNTRY_DETAILS_FAILURE,
    payload: error,
  };
};

export const fetchDomainApproverCountriesDetails = () => {
  return async function (dispatch) {
    // Check cache first
    const cachedCountries = safeParseCache();
    if (cachedCountries && cachedCountries.length > 0) {
      dispatch(fetchDomainApproverCountriesDetailsSuccess(cachedCountries));
      return cachedCountries;
    }

    dispatch(fetchDomainApproverCountriesDetailsRequest());
    try {
      const data = await APIManager.getDomainApproverCountries();
      persistCache(data);
      dispatch(fetchDomainApproverCountriesDetailsSuccess(data));
      return data;
    } catch (error) {
      dispatch(fetchDomainApproverCountriesDetailsFailure(error));
      dispatch(commonError(error, FETCH_DOMAIN_APPROVER_COUNTRY_DETAILS_FAILURE));
      throw error;
    }
  };
};

// Force refresh countries data by clearing cache and fetching fresh data
export const forceFreshCountriesDetails = () => {
  return async function (dispatch) {
    clearCache();
    dispatch(fetchDomainApproverCountriesDetailsRequest());
    try {
      const data = await APIManager.getDomainApproverCountries();
      persistCache(data);
      dispatch(fetchDomainApproverCountriesDetailsSuccess(data));
      return data;
    } catch (error) {
      dispatch(fetchDomainApproverCountriesDetailsFailure(error));
      dispatch(commonError(error, FETCH_DOMAIN_APPROVER_COUNTRY_DETAILS_FAILURE));
      throw error;
    }
  };
};

export const commonError=(error,errorType)=>{
  return({
    type:COMMON_ERROR,
    payload:{
      message:error.message,
      errorType:errorType
    }
  })
}
